import { COMPOSED_PROOF_SEED_MAX_CPU_MS } from '../spec/composedSynthesisBudget.js';
import type { MeasuredBuildExecutionV1 } from '../types.js';

/** The accepted wall window of every measured build with no media to bind, so
 * no final proof: one 20-minute end-to-end budget. */
export const MEASURED_BUILD_MAX_WALL_MS = 20 * 60_000;
export const MEASURED_BUILD_FINISH_RESERVE_MS = 30_000;

/** Composition media child wall window after release. Every provider step reserves its WORST
 * case (a 3-strip CLI batch is 600 s), so sequential steps need headroom far
 * beyond their typical duration; the parent deadline still caps the window. */
export const COMPOSITION_MEDIA_MAX_WALL_TIME_MS = 900_000;


/** A composed build with media spends its window in three sequential phases:
 * the structural proof (claim, seeding, compile, one gate round, proof
 * barrier), the media children released by the barrier, then the final proof
 * of the exact media-bound bytes (media binding, a second full gate round,
 * final proof, publication). Each phase gets an explicit budget, so the
 * window is DERIVED, never a fixed 20 minutes the media alone nearly fills.
 *
 * Both budgets are sized from the SLOWEST composed template, measured
 * locally by the composed Create certification and scaled by the
 * production/local ratios measured on 1983-mario (the only template with
 * production builds that reached the barrier):
 * - structural: production accepted→barrier 361 s, 322 s and 467 s (jobs
 *   fc8b94ba, 94214411, 08a1aa92) against 92 s locally, up to 5.1x;
 * - gate round: production 191 s, 160 s and 215 s against 75 s, up to 2.9x.
 * The slowest template is mine-platformer/bounty-bob: structural 211 s and
 * final 222 s locally, dominated by its workspace solver-proof test (83 s per
 * round: two ~7000-tick solver runs, sequential) plus playability (31 s) and
 * the native loop (55-59 s). That is ~1080 s and ~645 s in production.
 * 22 minutes is 1.2x the structural estimate; 14 minutes is 1.3x the final. */
export const COMPOSITION_STRUCTURAL_PROOF_BUDGET_MS = 22 * 60_000;
/** Final-proof budget after the media join (see the sizing note above). */
export const COMPOSITION_FINAL_PROOF_BUDGET_MS = 14 * 60_000;

/** Production/local ratio of one builder COMMAND gate (a tsc / vitest child
 * the Builder kills at its timeout): the slowest of 1983-mario's production
 * final gate round (job 1983-mario-composed-8, 2026-10-19) — tests 20.1 s
 * and playability 27.8 s against 7 s and 9 s locally. */
export const COMPOSITION_COMMAND_GATE_PRODUCTION_SLOWDOWN = 3.1;
/** The slowest command gate of any composed template, measured locally by
 * the composed Create certification (2026-10-19, machine load 5-8):
 * mine-platformer/bounty-bob's tests gate at 45-53 s, its two ~7000-tick
 * solver-proof seeds now proved in parallel test files (83 s when they ran
 * one after the other); its playability gate is 31-43 s. Production's
 * 180 s default cut that tests gate at 311 s into the 2026-10-19 build. */
export const COMPOSITION_SLOWEST_COMMAND_GATE_LOCAL_MS = 53_000;
/** How many times the slowest command gate's production estimate a
 * composition command gate's timeout allows (load, a second build sharing
 * the Builder, the test runner's own start-up). */
export const COMPOSITION_COMMAND_GATE_HEADROOM = 2;
/** Each command gate's timeout in a composition build:
 * {@link COMPOSITION_COMMAND_GATE_HEADROOM}x the longer of the slowest
 * gate's production estimate and the longest admitted proof seed
 * ({@link COMPOSED_PROOF_SEED_MAX_CPU_MS}: the tests gate re-runs each seed's
 * solver and idle matches as its own test file, whose wall time is at least
 * the seed's CPU), rounded up to a whole minute. A five-minute envelope
 * terminated Treasure Tunnel's assertion-free tests twice in production on
 * 2026-10-23; a seed admission accepts must never be one the gate cuts. Both
 * gate rounds still fit the structural phase. */
export const COMPOSITION_COMMAND_GATE_TIMEOUT_MS = Math.ceil(Math.max(
  COMPOSITION_SLOWEST_COMMAND_GATE_LOCAL_MS * COMPOSITION_COMMAND_GATE_PRODUCTION_SLOWDOWN, COMPOSED_PROOF_SEED_MAX_CPU_MS)
  * COMPOSITION_COMMAND_GATE_HEADROOM / 60_000) * 60_000;

/** Build-queue time a STARTED composed build with media may spend between
 * its request segments. The window starts at the build's first claim
 * (`startCompositionWindow`), so the queue in front of it is never spent; but
 * every segment yields and its successor claim waits for a free build slot,
 * and the queue runs two builds at once. Those waits get one whole composed
 * build (structural proof, media and final proof) of headroom. Wall time is
 * not a cost guard: spend stays bounded by the accepted provider-call and
 * cost limits. */
export const COMPOSITION_REQUEUE_ALLOWANCE_MS = COMPOSITION_STRUCTURAL_PROOF_BUDGET_MS + COMPOSITION_MEDIA_MAX_WALL_TIME_MS
  + COMPOSITION_FINAL_PROOF_BUDGET_MS;

type MediaWindow = { limits: { maxMediaWallTimeMs: number } };

/** The media a successor binds was published by its predecessor: it releases
 * no child, so its media phase takes no time and funds no provider. Its two
 * proofs still run, on bytes its parent never proved: a successor is repinned
 * to the current compiler and runtime release, and road-fighter v3 (2026-10-31)
 * was refused at 1187 s of the fixed window with its final proof unfinished. */
const COMPOSITION_INHERITED_MEDIA = { limits: { maxMediaWallTimeMs: 0, maxProviderCalls: 0, maxCostMicros: 0 } };

/** The media window of a composed successor: inherited when its authority
 * binds media; none when it is coded and so has no final proof. */
export function compositionSuccessorMediaWindow(authority: { media?: unknown }): typeof COMPOSITION_INHERITED_MEDIA | undefined {
  return authority.media ? COMPOSITION_INHERITED_MEDIA : undefined;
}

/** The parent wall window a measured build is accepted with. */
export function measuredBuildWallTimeMs(media: MediaWindow | undefined): number {
  return media
    ? COMPOSITION_REQUEUE_ALLOWANCE_MS + COMPOSITION_STRUCTURAL_PROOF_BUDGET_MS + media.limits.maxMediaWallTimeMs
      + COMPOSITION_FINAL_PROOF_BUDGET_MS + MEASURED_BUILD_FINISH_RESERVE_MS
    : MEASURED_BUILD_MAX_WALL_MS;
}

/** The exact limits of a zero-code-provider composition build: the only
 * provider allowance is its accepted fresh media, and the window is derived
 * from its media window. Every admission site writes these and every recheck
 * compares against them. */
export function compositionMeasuredLimits(
  media: { limits: { maxMediaWallTimeMs: number; maxProviderCalls: number; maxCostMicros: number } } | undefined,
): MeasuredBuildExecutionV1['limits'] {
  return {
    maxWallTimeMs: measuredBuildWallTimeMs(media),
    maxProviderCalls: media?.limits.maxProviderCalls ?? 0,
    maxCostMicros: media?.limits.maxCostMicros ?? 0,
    finishReserveMs: MEASURED_BUILD_FINISH_RESERVE_MS,
  };
}

/** Whether accepted limits are exactly {@link compositionMeasuredLimits}. */
export function isCompositionMeasuredLimits(
  limits: MeasuredBuildExecutionV1['limits'],
  media: Parameters<typeof compositionMeasuredLimits>[0],
): boolean {
  const expected = compositionMeasuredLimits(media);
  return limits.maxWallTimeMs === expected.maxWallTimeMs && limits.maxProviderCalls === expected.maxProviderCalls
    && limits.maxCostMicros === expected.maxCostMicros && limits.finishReserveMs === expected.finishReserveMs;
}

/** The instant a measured build's wall window started: `deadlineAt` is always
 * the window start plus `limits.maxWallTimeMs`, whether the start is the
 * acceptance (code builds) or a composed build's first segment claim
 * (`startCompositionWindow`). */
export function measuredBuildWindowStartAt(run: Pick<MeasuredBuildExecutionV1, 'deadlineAt' | 'limits'>): number {
  return run.deadlineAt - run.limits.maxWallTimeMs;
}

/** The latest instant the proof barrier may release media: the whole media
 * window and the final proof must still fit before the finish reserve. */
export function compositionMediaReleaseDeadlineAt(
  run: Pick<MeasuredBuildExecutionV1, 'deadlineAt' | 'limits'>,
  media: MediaWindow,
): number {
  return run.deadlineAt - run.limits.finishReserveMs - COMPOSITION_FINAL_PROOF_BUDGET_MS
    - media.limits.maxMediaWallTimeMs;
}

export const COMPOSITION_MEDIA_RELEASE_MISSED = 'Composition media cannot fit before the accepted parent deadline';
/** The worker's refusal when a request segment starts past its finish reserve. */
export const MEASURED_DEADLINE_EXHAUSTED = 'measured build accepted deadline exhausted before execution';

/** Whether a run that has not released its media yet can no longer do so.
 * The window runs from the first claim, so only the build's own work and its
 * waits between request segments spend it: the barrier refuses a late
 * release, and a request segment that starts already past this point fails
 * before any work instead of spending the build slot on a proof that cannot
 * be released. */
export function compositionMediaReleaseMissed(
  run: Pick<MeasuredBuildExecutionV1, 'deadlineAt' | 'limits' | 'compositionBarrier'>,
  media: MediaWindow,
  now: number,
): boolean {
  return !run.compositionBarrier && now > compositionMediaReleaseDeadlineAt(run, media);
}
